import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./WeightIndicators.css";
import { useProducts, type WeightIndicatorsSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const WeightIndicators: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products: indicators, loading, error } = useProducts<WeightIndicatorsSpecs>('weight-indicators', editMode);
  const navigate = useNavigate();

  const indicatorTypes = [
    {
      title: "Serie ZM",
      description:
        "Indicadores básicos y avanzados con múltiples opciones de conectividad.",
    },
    {
      title: "Serie E",
      description:
        "Indicadores programables y multi-función para aplicaciones versátiles.",
    },
    {
      title: "Serie WI",
      description:
        "Indicadores con display LED y características especializadas.",
    },
    {
      title: "Displays XR",
      description: "Displays remotos para visualización de peso a distancia.",
    },
    {
      title: "Para Básculas Camioneras",
      description: "Versiones GTN especializadas para pesaje de vehículos.",
    },
    {
      title: "Multi-Báscula",
      description:
        "Soporte para múltiples básculas y aplicaciones complejas.",
    },
  ];

  const applications = [
    {
      title: "Pesaje Industrial",
      description:
        "Control de peso en procesos de manufactura y producción.",
    },
    {
      title: "Dosificación",
      description:
        "Control preciso de cantidades en procesos automatizados.",
    },
    {
      title: "Control de Calidad",
      description: "Verificación de pesos y control de especificaciones.",
    },
    {
      title: "Aplicaciones Comerciales",
      description:
        "Pesaje para punto de venta y aplicaciones comerciales.",
    },
    {
      title: "Pesaje de Vehículos",
      description:
        "Sistemas especializados para básculas camioneras.",
    },
    {
      title: "Integración de Sistemas",
      description:
        "Conectividad con PLCs y sistemas de automatización.",
    },
  ];

  return (
    <div className="wi-page">
      {error && (
        <div className="wi-status-bar wi-status-bar--error">
          <p>Error al cargar productos. Por favor intente de nuevo.</p>
        </div>
      )}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="wi-hero">
        <div className="container wi-hero-content">
          <nav className="wi-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="wi-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="wi-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Indicadores de Peso</span>
          </nav>
          <h1 className="wi-hero-title">Indicadores de Peso</h1>
          <p className="wi-hero-subtitle">
            Displays y controladores electrónicos de alta precisión para
            sistemas de pesaje industriales.
          </p>
          {authType === 'internal' && (
            <button
              className={`wi-btn-edit-mode${editMode ? ' wi-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="wi-hero-image" aria-hidden="true">
          <img src="/images/indicators.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="wi-overview">
        <div className="container">
          <div className="wi-overview-grid">
            <div className="wi-overview-text">
              <span className="wi-label">Descripción general</span>
              <h2>Precisión y conectividad en cada lectura</h2>
              <p>
                Los indicadores de peso de ABC Básculas combinan pantallas de
                alta resolución con firmware robusto y amplias opciones de
                conectividad. Compatibles con celdas de carga analógicas y
                digitales, se integran fácilmente con PLCs, sistemas ERP y
                software de gestión de datos para ofrecer visibilidad total de
                su proceso productivo.
              </p>
            </div>
            <div className="wi-overview-image">
              
            </div>
          </div>
        </div>
      </section>

      {/* ── Products ─────────────────────────────────────────── */}
      <section className="wi-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Modelos Disponibles</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/weight-indicators')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : indicators.map((indicator, index) => (
                <ProductCard
                  key={index}
                  reviewed={indicator.reviewed}
                  endOfLife={indicator.endOfLife}
                  productId={indicator.productId}
                  name={indicator.name}
                  badge={`Modelo ${indicator.specs?.model ?? ''}`}
                  sub={
                    (indicator.specs?.connectivity ?? []).length > 0
                      ? (indicator.specs?.connectivity ?? [])[0] ?? undefined
                      : undefined
                  }
                  brand="AWTX"
                  description={indicator.specs?.description ?? undefined}
                  image={indicator.image}
                  onRevisar={editMode && indicator.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(indicator.specs?.presentations?.length
                      ? [{ icon: '🖥️', label: 'Presentaciones', items: (indicator.specs?.presentations ?? []).filter((x): x is string => x != null) }]
                      : []),
                    ...(indicator.specs?.features?.length
                      ? [{ icon: '⚙️', label: 'Características', items: (indicator.specs?.features ?? []).filter((x): x is string => x != null) }]
                      : []),
                    ...(indicator.specs?.applications?.length
                      ? [{ icon: '🏭', label: 'Aplicaciones', items: (indicator.specs?.applications ?? []).filter((x): x is string => x != null) }]
                      : []),
                    ...(indicator.specs?.connectivity?.length
                      ? [{ icon: '🔌', label: 'Conectividad', items: (indicator.specs?.connectivity ?? []).filter((x): x is string => x != null) }]
                      : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Indicator types ──────────────────────────────────── */}
      <section className="wi-types">
        <div className="container">
          <span className="wi-label">Familias de producto</span>
          <h2 className="wi-section-title">Tipos de Indicadores</h2>
          <div className="wi-types-grid">
            {indicatorTypes.map((type, index) => (
              <div key={index} className="wi-type-card">
                <h3>{type.title}</h3>
                <p>{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="wi-applications">
        <div className="container">
          <span className="wi-label wi-label--light">Industrias</span>
          <h2 className="wi-section-title wi-section-title--light">
            Aplicaciones Industriales
          </h2>
          <div className="wi-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="wi-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WeightIndicators;
